import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Pagination,
  Tooltip,
  Box,
  Typography,
  alpha,
} from "@mui/material";
import TableSkeleton from "./TableSkeleton";

const TableComponent = ({
  columns,
  data,
  actions,
  isLoading,
  pagination,
  onPageChange,
  size = "small",
  rowClassName,
  getRowSx,
}) => {
  if (isLoading) {
    return <TableSkeleton columns={columns} actions={actions} size={size} />;
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: "1px solid #F2F4F7",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.04)",
        background: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <Table size={size}>
        <TableHead>
          <TableRow sx={{ bgcolor: "#F8FAFC" }}>
            {columns.map((col) => (
              <TableCell
                key={col.field}
                sx={{
                  fontWeight: 600,
                  color: "#475467",
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  py: 1.5,
                  px: 1.5,
                  borderBottom: "1px solid #F2F4F7",
                }}
              >
                {col.headerName}
              </TableCell>
            ))}
            {actions?.length > 0 && (
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  color: "#475467",
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.3px",
                  py: 1.5,
                  px: 1.5,
                  borderBottom: "1px solid #F2F4F7",
                }}
              >
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {data?.length > 0 ? (
            data.map((row, rowIndex) => (
              <TableRow
                key={row.id || rowIndex}
                className={rowClassName}
                sx={{
                  ...(getRowSx ? getRowSx(row, rowIndex) : {}),
                  "&:hover": {
                    bgcolor: alpha("#2469F0", 0.02),
                  },
                  "&:last-child td": { borderBottom: 0 },
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    sx={{
                      color: "#1A1A2E",
                      fontSize: "0.78rem",
                      py: 0.75,
                      px: 1.5,
                      borderBottom: "1px solid #F2F4F7",
                    }}
                  >
                    {col.render ? col.render(row[col.field], row) : (row[col.field] || "-")}
                  </TableCell>
                ))}

                {actions?.length > 0 && (
                  <TableCell
                    align="center"
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      borderBottom: "1px solid #F2F4F7",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 0.25, justifyContent: "center" }}>
                      {actions.map((action, index) =>
                        action.element ? (
                          <React.Fragment key={index}>
                            {action.element(row)}
                          </React.Fragment>
                        ) : (
                          <Tooltip key={index} title={action.tooltip || ""} arrow>
                            <IconButton
                              size="small"
                              onClick={() => action.handler(row)}
                              sx={{
                                color: "#667085",
                                p: 0.5,
                                "&:hover": {
                                  color: action.color === "error" ? "#EF4444" : "#2469F0",
                                  bgcolor: action.color === "error"
                                    ? alpha("#EF4444", 0.06)
                                    : alpha("#2469F0", 0.06),
                                },
                              }}
                            >
                              {React.cloneElement(action.icon, { sx: { fontSize: 16 } })}
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length + (actions?.length ? 1 : 0)}
                align="center"
                sx={{ py: 4 }}
              >
                <Typography color="text.secondary" sx={{ fontSize: "0.9rem" }}>
                  No data available
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {pagination && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.5,
            bgcolor: "#FAFBFC",
            borderTop: "1px solid #F2F4F7",
          }}
        >
          <Typography variant="caption" sx={{ color: "#667085", fontWeight: 500 }}>
            Page {pagination.currentPage} of {pagination.totalPages}
          </Typography>
          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={onPageChange}
            size="small"
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": {
                borderRadius: "6px",
                fontWeight: 500,
                fontSize: "0.75rem",
                color: "#475467",
                "&.Mui-selected": {
                  bgcolor: "#2469F0",
                  color: "#fff",
                  "&:hover": {
                    bgcolor: "#1A5BD4",
                  },
                },
                "&:hover": {
                  bgcolor: "#F2F4F7",
                },
              },
            }}
          />
        </Box>
      )}
    </TableContainer>
  );
};

export default TableComponent;